import { DashboardPages } from '../common/components/dashboards/types';
import { Filter } from '../common/components/filter/filterbar/types';
import { Granularity, Months } from '../common/components/timeslider/types';
import { CustomError } from '../common/errors';
import { Domains, PartnerDomains } from '../constants';
import {
  DataFields,
  DataFieldWithDataType,
  DataTypes,
  DateAsYYYYMMDD,
} from '../types';
import { fnvHash } from '../utils';
import {
  CohortMetricId,
  Confidential,
  EmployeeCohortMetricId,
  MetricCategoryId,
  MetricGroupId,
  MetricId,
  RegularMetricId,
} from './types-graphql';

export interface HasDependencies {
  getDeps: () => (string | undefined)[];
}

export const hashString = (s: (string | undefined)[]): string =>
  fnvHash(s.join(',')).hash.toString();

export interface ApiError extends CustomError {
  errorReferences: string[] | null;
}

export interface TimeSliderConfig {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  rangeStartDate: string | null;
  rangeEndDate: string | null;
  specificPeriodBenchmarkStartDate: string | null;
  specificPeriodBenchmarkEndDate: string | null;
  firstMonthOfYear: Months;
}

export type RoleId = string;

export interface Role {
  id: RoleId;
  name: string;
  domain: string | null;
}

export type ExecutorRole = Role;
export type SimulatedRole = Role;
export type UserRole = Role;

export type Date = string;
export type SQLQuery = string;
export type SQLFilters = string;
export type VersionId = string;
export type Timestamp = string;
export type DataValue =
  | number
  | boolean
  | string
  | Date
  | Confidential
  | MetricDataJson
  | null;
export type MetricDataJson = JSON; // This is the json that a metric returns as data. Usually metrics return numbers, but some can return json
export type SqlQueryDataJson = JSON; // This is the json that a custom sql query returns as data

export type ApiValueDataType =
  | { __typename: 'BigDecimalValue'; bigDecimal: any }
  | { __typename: 'BooleanValue'; boolean: boolean }
  | { __typename: 'Confidential'; reason: string | null }
  | { __typename: 'DateValue'; date: DateAsYYYYMMDD }
  | { __typename: 'DoubleValue'; double: number }
  | { __typename: 'IntValue'; int: number }
  | { __typename: 'StringValue'; string: string }
  | { __typename: 'TimestampValue'; timestamp: any }
  | { __typename: 'JsonValue'; json: MetricDataJson }
  | null;

export type ApiTimeSegmentType =
  | ApiOverTimeTimeSegmentType
  | ApiTimePeriodTimeSegmentType;

export type CalendarYearMonthlySegment = {
  __typename: 'CalendarYearMonthlySegment';
  date: DateAsYYYYMMDD;
};

export type CalendarYearQuarterlySegment = {
  __typename: 'CalendarYearQuarterlySegment';
  quarter: { __typename?: 'Quarter'; quarterOfYear: number; year: number };
};

export type CalendarYearYearlySegment = {
  __typename: 'CalendarYearYearlySegment';
  year: number;
};

export type FinancialYearQuarterlySegment = {
  __typename: 'FinancialYearQuarterlySegment';
  quarter: { __typename?: 'Quarter'; quarterOfYear: number; year: number };
};

export type FinancialYearYearlySegment = {
  __typename: 'FinancialYearYearlySegment';
  year: number;
};

export type ApiOverTimeTimeSegmentType =
  | CalendarYearMonthlySegment
  | CalendarYearQuarterlySegment
  | CalendarYearYearlySegment
  | FinancialYearQuarterlySegment
  | FinancialYearYearlySegment;

export type ApiTimePeriodTimeSegmentType =
  | SingleValueTimeSegment
  | CalendarYearSingleValueByMonthsSegment
  | CalendarYearSingleValueByYearsSegment
  | FinancialYearSingleValueByQuartersSegment
  | FinancialYearSingleValueByYearsSegment;

export interface SingleValueTimeSegment {
  __typename: 'SingleValueTimeSegment';
  end: DateAsYYYYMMDD;
  start: DateAsYYYYMMDD;
}

export interface CalendarYearSingleValueByMonthsSegment {
  __typename: 'CalendarYearSingleValueByMonthsSegment';
  end: DateAsYYYYMMDD;
  start: DateAsYYYYMMDD;
}

export interface CalendarYearSingleValueByYearsSegment {
  __typename: 'CalendarYearSingleValueByYearsSegment';
  year: number;
}

export interface FinancialYearSingleValueByQuartersSegment {
  __typename: 'FinancialYearSingleValueByQuartersSegment';
  quarter: { __typename?: 'Quarter'; quarterOfYear: number; year: number };
}

export interface FinancialYearSingleValueByYearsSegment {
  __typename: 'FinancialYearSingleValueByYearsSegment';
  year: number;
}

export interface ApiMetricResults {
  __typename?: 'MetricResults';
  results: Array<QueryMetricQueryFailure | QueryMetricsQuerySuccess>;
}

export interface QueryMetricQueryFailure {
  __typename: 'MetricResultFailure';
  message: string;
  errorReference: string | null;
  meta: {
    __typename?: 'MetricResultMetaData';
    metricSql: string | null;
  };
}

export interface QueryMetricsQuerySuccess {
  __typename: 'MetricResultSuccess';
  metricId: RegularMetricId | CohortMetricId | EmployeeCohortMetricId;
  segments: Array<{
    __typename?: 'MetricTimeSegmentData';
    groupSegments: Array<{
      __typename?: 'MetricGroupSegmentData';
      data: ApiValueDataType;
      groupSegment: Array<{
        __typename?: 'ByFieldSegment';
        dataField: any;
        dataType: any;
        value: ApiValueDataType;
      }>;
    }>;
    timeSegment: ApiTimeSegmentType;
  }>;
  meta: { __typename?: 'MetricResultMetaData'; metricSql: string | null };
}

export interface GroupSegment {
  dataField: DataFields;
  dataType: DataTypes;
  // Double check this
  value: DataValue | null;
}

interface GroupSegmentsResult {
  data: DataValue;
  groupSegment: GroupSegment[];
}

export interface Segment {
  groupSegments: GroupSegmentsResult[];
  timeSegment: ApiTimeSegmentType;
}

export interface CohortMetricResult {
  type: 'CohortMetricResult';
  metricId: CohortMetricId;
  segments: Segment[];
  meta: {
    metricSql: string | null;
  };
}

export interface EmployeeCohortMetricResult {
  type: 'EmployeeCohortMetricResult';
  metricId: EmployeeCohortMetricId;
  segments: Segment[];
  meta: {
    metricSql: string | null;
  };
}

export interface RegularMetricResult {
  type: 'RegularMetricResult';
  metricId: RegularMetricId;
  segments: Segment[];
  meta: {
    metricSql: string | null;
  };
}

export type MetricResult =
  | RegularMetricResult
  | CohortMetricResult
  | EmployeeCohortMetricResult;
export type MetricResults = RegularMetricResult[] | CohortMetricResult[];

export enum ChartTypes {
  BAR = 'bar',
  DOUGHNUT = 'doughnut',
  HORIZONTALBAR = 'horizontalBar',
  LINE = 'line',
}

export enum ChartPositions {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

interface ChartConfig {
  chartDimension: DataFieldWithDataType;
  chartType?: ChartTypes;
  percentChartWidth?: number;
  isHorizontal?: boolean;
}

export interface ChartSettings {
  [ChartPositions.TOP]: ChartConfig[];
  [ChartPositions.BOTTOM]: ChartConfig[];
  topRowPercentHeight?: number;
}

export type DEFAULT_OPTION = 'default';
export const DEFAULT_OPTION = 'default' as const;

export type DashboardChartSettings = Record<
  DashboardPages | DEFAULT_OPTION,
  ChartSettings
>;

export type NonCoreDashboardPageToChartSettingKeys =
  | DashboardPages.PAYROLL_COST_TO_COMPANY
  | DashboardPages.RECRUITMENT_ACTIVITIES;

export type NonCoreDashboardChartSettings = Record<
  NonCoreDashboardPageToChartSettingKeys,
  ChartSettings
>;

export interface DashboardSetting {
  chartSettings?: DashboardChartSettings;
}

export interface NonCoreDashboardSetting {
  chartSettings?: NonCoreDashboardChartSettings;
}

interface EvaluationSettings {
  evaluationCycles?: PerformanceCycle[];
}

interface AgeGroups {
  description: string;
  minValueInMonth: number;
}

interface TenureGroups {
  description: string;
  minValueInMonth: number;
}

interface FilterGroup {
  name: string;
  filters: Filter[];
}

interface SortSettings {
  field: DataFieldWithDataType;
  sortOrder: string[];
}

interface MetricGroupCategorySetting {
  metricGroup: MetricGroupId;
  category: MetricCategoryId;
}

export enum AttritionTypes {
  ANNUALISED_ATTRITION = 'annualisedAttrition',
  MONTHLY_ATTRITION = 'monthlyAttrition',
  TTM_ATTRITION = 'trailingTwelveMonthsAttrition',
  STARTING_POP_ATTRITION = 'startingPopAttrition',
}

export enum MedianBaseToggleOptions {
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
}

interface AccountRestrictions {
  noRestrictions?: any;
  ipWhitelist?: {
    ips: string[];
  } | null;
}

interface ApiRequestRestrictions {
  serviceAccountRestrictions?: AccountRestrictions | null;
  userAccountRestrictions?: AccountRestrictions | null;
}

interface DataUpdateNotifications {
  microsoftTeams: {
    enabled: boolean;
    webhookUrl: string;
  };
  slack: {
    enabled: boolean;
  };
  email: {
    enabled: boolean;
  };
}

export interface DomainPreferences {
  defaultLang: string;
  autoLogoutTimeInMinutes?: number | null;
  icrPopulationProperties: string[];
  warningToLogoutTimeInMinutes?: number | null;
  timeSliderRangeStart?: string | null;
  allowedFilters?: DataFieldWithDataType[] | null;
  allowedMetricIds?: MetricId[] | null;
  metricGroupCategorySettings?: MetricGroupCategorySetting[] | null;
  ageGroups?: AgeGroups[] | null;
  rootEmpId?: string | null;
  tenureGroups?: TenureGroups[] | null;
  evaluationSettings?: EvaluationSettings | null;
  dashboardSettings?: DashboardSetting | null;
  nonCoreDashboardSettings?: NonCoreDashboardSetting | null;
  defaultAttritionType?: AttritionTypes | null;
  finYearStartMonth?: Months | null;
  paidLeaveCycleStartMonth?: Months | null;
  ot36CycleStartMonth?: Months | null;
  isUsingEffectiveLeaverDate?: boolean | null;
  medianBaseToggle?: MedianBaseToggleOptions | null;
  referenceCurrency?: string | null;
  filterGroups?: FilterGroup[] | null;
  sortSettings?: SortSettings[];
  customLandingPage?: string;
  apiRequestRestrictions?: ApiRequestRestrictions | null;
  dataUpdateNotifications?: DataUpdateNotifications;
}

export interface LatestDomainPreferences {
  createdAt: string;
  creator: string | null; // email
  settings: DomainPreferences;
  version: number;
}

export enum EditPermissionTabs {
  POPULATION = 'population',
  DATA = 'data',
  DASHBOARDS = 'dashboards',
  METRICSFILTERS = 'metricsFilters',
}

export interface AllowedDashboard {
  id: string;
}

export interface DomainSettings {
  domain: Domains;
  name: string;
  logoURL?: string;
  address?: string;
  customLandingPage?: string;
  allowedDashboards: AllowedDashboard[];
  canUsePermissionsUi?: boolean;
  allowedEditPermissionTabs?: EditPermissionTabs[];
  domainOfPartner?: PartnerDomains;
  alias?: string;
}

export interface LatestDomainSettings {
  createdAt: string;
  creator: string | null; // email
  settings: DomainSettings;
  version: number;
}

type CycleFilterValuesToScoreRangeMap = Record<string, number>;

export interface PerformanceCycle {
  cycleDate: string;
  cycleName: string;
  cycleType: string;
  cycleDateLabel?: string;
  cycleVizType: string;
  scoreSortingOrder?: string[] | undefined;
  evaluationValuesToScoreMap: CycleFilterValuesToScoreRangeMap;
}

export interface AllowedDomain {
  domain: Domains;
  latestPreferences: LatestDomainPreferences;
  latestSettings: LatestDomainSettings;
}

export enum GraphQlRequestErrorCode {
  query = 'query',
  Err_AuthorizationDenied = 'Err_AuthorizationDenied',
  Err_RoleNotFound = 'Err_RoleNotFound',
  Err_UserNotFound = 'Err_UserNotFound',
  Err_EmployeeNotFound = 'Err_EmployeeNotFound',
  Err_OrganizationPathNotFound = 'Err_OrganizationPathNotFound',
  Err_NoLatestVersionFound = 'Err_NoLatestVersionFound',
  Err_NoFirstVersionFound = 'Err_NoFirstVersionFound',
  Err_ReportNotFound = 'Err_ReportNotFound',
  Err_DashboardNotFound = 'Err_DashboardNotFound',
  Err_AliasNotFound = 'Err_AliasNotFound',
  Err_PermissionIdNotFound = 'Err_PermissionIdNotFound',
  Err_DomainSettingsNotFound = 'Err_DomainSettingsNotFound',
  Err_DomainSettingsVersionNotFound = 'Err_DomainSettingsVersionNotFound',
  Err_DomainPreferencesNotFound = 'Err_DomainPreferencesNotFound',
  Err_DomainPreferencesVersionNotFound = 'Err_DomainPreferencesVersionNotFound',
  Err_RolePreferencesNotFound = 'Err_RolePreferencesNotFound',
  Err_RolePreferencesVersionNotFound = 'Err_RolePreferencesVersionNotFound',
  Err_UserAlreadyExists = 'Err_UserAlreadyExists',
  Err_MetaDataError = 'Err_MetaDataError',
  Err_SameNameRoleAlreadyExists = 'Err_SameNameRoleAlreadyExists',
  Err_QueryExecutionError = 'Err_QueryExecutionError',
  Err_MultipleEmployeesForId = 'Err_MultipleEmployeesForId',
  Err_AmbiguousPopFilter = 'Err_AmbiguousPopFilter',
  Err_NoReadDataPermissions = 'Err_NoReadDataPermissions',
  Err_AmbiguousReadDataPermissions = 'Err_AmbiguousReadDataPermissions',
  Err_DeleteFileFailed = 'Err_DeleteFileFailed',
  Err_ListFilesFailed = 'Err_ListFilesFailed',
  Err_GetBucketFailed = 'Err_GetBucketFailed',
  Err_CreateBucketFailed = 'Err_CreateBucketFailed',
  Err_CreateFolderFailed = 'Err_CreateFolderFailed',
  Err_CreateFoldersFailed = 'Err_CreateFoldersFailed',
  Err_FilterInvalid = 'Err_FilterInvalid',
  Err_SendingEmailError = 'Err_SendingEmailError',
  Err_ExternalSystemUserCreationError = 'Err_ExternalSystemUserCreationError',
  Err_ResetPasswordError = 'Err_ResetPasswordError',
  Err_ValidationFailed = 'Err_ValidationFailed',
  Err_Unspecified = 'Err_Unspecified',
  Err_DomainAccessDeniedWrongAuthMethod = 'Err_DomainAccessDeniedWrongAuthMethod',
  Err_DomainAccessDeniedNotEnabled = 'Err_DomainAccessDeniedNotEnabled',
  Err_RateLimitExceeded = 'Err_RateLimitExceeded',
  Err_DomainAccessDeniedIpForbidden = 'Err_DomainAccessDeniedIpForbidden',
}

export type ErrorReference = string;

export interface GraphQlError extends Error {
  response: {
    errors: Array<{
      extensions: {
        errorCode: GraphQlRequestErrorCode;
        errorReference: ErrorReference;
      };
    }>;
  };
}
